// LearnCore Frontend - 任务更新实时同步Context

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tasksApi, dkpApi, statisticsApi } from '../api/tasks';
import type { UserTask, DKPBalance as DKPBalanceType } from '../types/tasks-rewards';

interface TaskUpdatesContextType {
  tasks: UserTask[];
  dkpBalance: DKPBalanceType | null;
  statistics: any | null;
  loading: boolean;
  error: string | null;
  refreshTasks: () => Promise<void>;
  refreshDKP: () => Promise<void>;
  refreshStatistics: () => Promise<void>;
  refreshAll: () => Promise<void>;
  updateTaskOptimistic: (taskId: string, updates: Partial<UserTask>) => void;
}

const TaskUpdatesContext = createContext<TaskUpdatesContextType | undefined>(undefined);

export const useTaskUpdates = () => {
  const context = useContext(TaskUpdatesContext);
  if (!context) {
    throw new Error('useTaskUpdates must be used within TaskUpdatesProvider');
  }
  return context;
};

interface TaskUpdatesProviderProps {
  children: React.ReactNode;
  userId?: string;
  autoRefresh?: boolean;
  refreshInterval?: number; // seconds
}

export const TaskUpdatesProvider: React.FC<TaskUpdatesProviderProps> = ({
  children,
  userId,
  autoRefresh = true,
  refreshInterval = 30
}) => {
  const [tasks, setTasks] = useState<UserTask[]>([]);
  const [dkpBalance, setDkpBalance] = useState<DKPBalanceType | null>(null);
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 刷新任务列表
  const refreshTasks = useCallback(async () => {
    try {
      setError(null);
      const response = await tasksApi.getTasks();
      if (response && response.tasks) {
        setTasks(response.tasks || []);
      }
    } catch (err: any) {
      console.error('刷新任务列表失败:', err);
      setError(err.response?.data?.error || '刷新任务列表失败');
    }
  }, [userId]);

  // 刷新DKP余额
  const refreshDKP = useCallback(async () => {
    try {
      setError(null);
      const response = await dkpApi.getDKPBalance(userId);
      if (response && response.balance) {
        setDkpBalance(response.balance);
      }
    } catch (err: any) {
      console.error('刷新DKP余额失败:', err);
      setError(err.response?.data?.error || '刷新DKP余额失败');
    }
  }, [userId]);

  // 刷新统计数据
  const refreshStatistics = useCallback(async () => {
    try {
      setError(null);
      const response = await statisticsApi.getTaskStatistics({ period: 'week' });
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (err: any) {
      console.error('刷新统计数据失败:', err);
      setError(err.response?.data?.error || '刷新统计数据失败');
    }
  }, [userId]);

  // 刷新所有数据
  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        refreshTasks(),
        refreshDKP(),
        refreshStatistics()
      ]);
    } finally {
      setLoading(false);
    }
  }, [refreshTasks, refreshDKP, refreshStatistics]);

  // 乐观更新任务
  const updateTaskOptimistic = useCallback((taskId: string, updates: Partial<UserTask>) => {
    setTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, ...updates } : task
    ));
  }, []);

  // 初始化数据
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // 自动刷新
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      refreshAll();
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refreshAll]);

  // 页面可见性变化时刷新
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshAll();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userId, refreshAll]);

  // 窗口获得焦点时刷新
  useEffect(() => {
    const handleFocus = () => {
      refreshAll();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [userId, refreshAll]);

  const value: TaskUpdatesContextType = {
    tasks,
    dkpBalance,
    statistics,
    loading,
    error,
    refreshTasks,
    refreshDKP,
    refreshStatistics,
    refreshAll,
    updateTaskOptimistic
  };

  return (
    <TaskUpdatesContext.Provider value={value}>
      {children}
    </TaskUpdatesContext.Provider>
  );
};

// 导出便捷的Hook用于监听特定任务的变化
export const useTaskRealtimeUpdates = (taskId: string) => {
  const { tasks, updateTaskOptimistic } = useTaskUpdates();

  const task = tasks.find(t => t.id === taskId);

  const updateTask = useCallback((updates: Partial<UserTask>) => {
    if (taskId) {
      updateTaskOptimistic(taskId, updates);
    }
  }, [taskId, updateTaskOptimistic]);

  return {
    task,
    updateTask,
    isLoading: !task
  };
};

// 导出便捷的Hook用于监听DKP变化
export const useDKPRealtimeUpdates = () => {
  const { dkpBalance, refreshDKP, loading } = useTaskUpdates();

  return {
    balance: dkpBalance,
    refresh: refreshDKP,
    loading
  };
};

// 导出便捷的Hook用于监听统计数据变化
export const useStatisticsRealtimeUpdates = () => {
  const { statistics, refreshStatistics, loading } = useTaskUpdates();

  return {
    statistics,
    refresh: refreshStatistics,
    loading
  };
};